import { Box, Typography } from '@mui/material';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Button, ModalContentContainer, TextField } from '@/components';
import { useForm } from '@/hooks';
import { EVENTS } from '@/services';
import { selectUserProfileResult, useUpdateUserProfileMutation } from '@/store';

type EditEmailFormState = {
  email: string;
};

export const EditEmail = ({
  onSuccess,
  onCancel,
}: {
  onSuccess?: () => void;
  onCancel?: () => void;
}) => {
  const [updateProfile] = useUpdateUserProfileMutation();
  const {
    form,
    setForm,
    errors,
    setErrors,
    onChange,
    validateForm,
    resetForm,
  } = useForm<EditEmailFormState>({
    email: '',
  });
  const { data: profile } = useSelector(selectUserProfileResult);

  const onSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    }
    if (!profile?.firstName || !profile?.lastName) {
      setErrors({
        email: 'Something went wrong. Please try again or contact support.',
      });
      return;
    }
    if (profile?.email === form.email.trim()) {
      setErrors({
        email: 'This email address is already in use.',
      });
      return;
    }
    try {
      await updateProfile({
        email: form.email.trim(),
        firstName: profile?.firstName,
        lastName: profile?.lastName,
        event: EVENTS.PROFILE_EVENTS.UPDATE_EMAIL,
      }).unwrap();
      onSuccess?.();
    } catch (error) {
      const { status } = error as FetchBaseQueryError;
      if (status === 409) {
        setErrors({
          email: 'This email address is already in use.',
        });
      } else {
        setErrors({
          email: 'Something went wrong. Please try again or contact support.',
        });
      }
    }
  };

  useEffect(() => {
    if (profile?.email) {
      setForm({
        email: profile?.email,
      });
    }
  }, [profile, setForm]);

  return (
    <ModalContentContainer>
      <Typography variant="h1">Edit Email</Typography>
      <TextField
        id="email"
        fullWidth
        label="Account Email"
        placeholder="Enter your email"
        type="email"
        sx={{ mt: 6 }}
        value={form.email}
        error={!!errors.email}
        helperText={errors.email}
        onChange={onChange}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          mt: 4,
        }}>
        <Button
          variant="secondary"
          label="Cancel"
          fullWidth={false}
          skinny={false}
          sx={{
            width: '50%',
          }}
          onClick={() => {
            onCancel?.();
            resetForm();
          }}
        />
        <Button
          variant="primary"
          label="Save"
          fullWidth={false}
          skinny={false}
          sx={{ width: '50%' }}
          onClick={onSubmit}
        />
      </Box>
    </ModalContentContainer>
  );
};
